import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IInternalUser } from '@core/model';
import { UsersService } from '@core/services';
import { UtilitiesService } from '@core/services/utilities.service';
import { FeatureToggleService } from '@mynexus/mynexus-ui-lib';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-user-profile-widget',
    templateUrl: './user-profile-widget.component.html',
    styleUrls: ['./user-profile-widget.component.scss'],
    standalone: false
})
export class UserProfileWidgetComponent implements OnInit {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userService = inject(UsersService);
  private readonly utilitiesService = inject(UtilitiesService);

  userId = '';
  user!: IInternalUser;
  isLoading = false;
  form!: FormGroup;
  // tslint:disable-next-line: no-any
  initialFormValue!: any;
  isFormChanged = false;
  isChangeUserEnabled = false;

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.userId = params.uid);

    this.getUserById(this.userId);

    this.featureToggleService.getFeatureAccess('User_Management_Change_User_Type')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.isChangeUserEnabled = isEnabled;
      });
  }

  getUserById(userId: string): void {
    this.isLoading = true;
    this.userService
      .getInternalUserById(userId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading = false)
      )
      .subscribe(user => {
        this.user = user;
        this.user.isActive = this.user.isApproved;
        this.user.userId = userId;
        this.createForm(this.user);
      });
  }

  goBack(): void {
    this.router.navigate(['users'], { queryParams: { index: 1 } });
  }

  onSave(): void {
    const data = { ...this.form.value };
    const internalUser = {
      isApproved: this.user.isActive,
      firstName: data.givenName,
      lastName: data.surname,
      displayName: `${data.givenName} ${data.surname}`,
      credentials: data.credentials
    } as IInternalUser;

    this.userService.saveInternalUserById(this.userId, internalUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.messageService.add({
          severity: 'success',
          key: 'saved',
          detail: `All changes were saved successfully`,
          life: 5000
        });
        this.getUserById(this.userId);
        this.isFormChanged = false;
        this.utilitiesService.hasUnsavedChanges = !this.canSaveUser;
      });
  }

  onCancel(): void {
    if (this.form) {
      this.form.get('givenName')?.setValue(this.user.firstName);
      this.form.get('surname')?.setValue(this.user.lastName);
      this.form.get('credentials')?.setValue(this.user.credentials);
      this.form.get('otherMails')?.setValue(this.user.email);
    }
  }

  createForm(user: IInternalUser): void {
    this.form = new FormGroup({
      givenName: new FormControl(user.firstName, Validators.required),
      surname: new FormControl(user.lastName, Validators.required),
      credentials: new FormControl(user.credentials),
      otherMails: new FormControl({ disabled: true, value: user.email })
    });
    this.initialFormValue = Object.assign({}, this.form.value);
    this.form.valueChanges.subscribe(() => this.handleFormChange());
  }

  handleFormChange(): void {
    this.isFormChanged = this.formChanged();
    this.utilitiesService.hasUnsavedChanges = !this.canSaveUser;
  }

  formChanged(): boolean {
    return (
      Object.keys(this.initialFormValue).filter(
        key => this.initialFormValue[key] !== this.form.value[key]
      ).length > 0
    );
  }

  get canSaveUser(): boolean {
    return !(this.isFormChanged && this.form.valid);
  }

  changeUserType() {
    this.confirmationService.confirm({
      header: 'Change User Type',
      message: 'Changing your user type should only be performed under specific, authorized circumstances and should not be done as a general configuration change.\nThis will convert your internal account into an external account. Are you sure you want to continue?',
      acceptLabel: 'Change User Type',
      rejectLabel: 'Cancel',
      dismissableMask: true,
      accept: () => {
        this.userService.changeUserType(this.userId, true)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Successfully Changed User Type:',
                key: 'saved',
                detail: `The internal account has been changed into an external account. You will now be redirected back to the home page.`,
                life: 2000
              });

              setTimeout(() => {
                this.goBack();
              }, 2000);
            },
            error: () => {
              this.messageService.add({
                severity: 'error',
                summary: 'Unable to Change User Type',
                key: 'error',
                detail: `An Error has occurred when changing the user type. Please try again later.`,
                life: 5000
              });
            }
          });
      }
    });
  }
}
